// src/components/layout/Footer.js

import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../../context/ThemeContext';
import logos from '../../assets/images/logo.png';

// Conteneur du footer avec courbe en partie supérieure
const FooterContainer = styled.footer`
  position: relative;
  padding: 0 0 2rem 0;
  margin-top: 6rem;
  border-radius: 0;
  overflow: hidden;
  color: ${props => props.isDark ? '#E2E8F0' : '#2D3748'};
  background: ${props => props.isDark 
    ? 'linear-gradient(180deg, rgba(10, 24, 61, 0.95) 0%, rgba(15, 35, 75, 0.98) 100%)' 
    : 'linear-gradient(180deg, #F0F5FF 0%, #E6EEFF 100%)'
  };
  box-shadow: ${props => props.isDark 
    ? '0 -10px 30px rgba(0, 0, 0, 0.2)' 
    : '0 -10px 30px rgba(0, 0, 0, 0.05)'
  };
  
  /* Effet de lueur subtile */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.isDark 
      ? 'radial-gradient(circle at 70% 30%, rgba(66, 153, 225, 0.08) 0%, transparent 70%)' 
      : 'radial-gradient(circle at 70% 30%, rgba(66, 153, 225, 0.1) 0%, transparent 70%)'
    };
    pointer-events: none;
    z-index: 1;
  }
`;

// Vagues décoratives en haut du footer avec double couche pour effet de profondeur
const FooterWaveContainer = styled.div`
  position: absolute;
  top: -100px;
  left: 0;
  width: 100%;
  height: 100px;
  overflow: hidden;
  z-index: 2;
  pointer-events: none;
`;

const FooterWaveTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z' fill='${encodeURIComponent(
    props => props.isDark ? '%230A1845' : '%23F0F5FF'
  )}'/%3E%3C/svg%3E");
  background-size: cover;
  background-position: center;
  z-index: 3;
  opacity: 0.8;
`;

const FooterWaveBottom = styled.div`
  position: absolute;
  top: 10px;
  left: 0;
  width: 100%;
  height: 100px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' fill='${encodeURIComponent(
    props => props.isDark ? '%230C214E' : '%23E6EEFF'
  )}'/%3E%3C/svg%3E");
  background-size: cover;
  background-position: center;
  z-index: 2;
  opacity: 0.6;
`;

// Éléments décoratifs sur les côtés (cercles et lignes)
const FooterDecoration = styled.div`
  position: absolute;
  width: 250px;
  height: 250px;
  border-radius: 50%;
  border: 1px solid ${props => props.isDark ? 'rgba(66, 153, 225, 0.15)' : 'rgba(66, 153, 225, 0.2)'};
  opacity: 0.6;
  
  &.top-right {
    top: -50px;
    right: -100px;
    width: 300px;
    height: 300px;
    border-width: 2px;
  }
  
  &.bottom-left {
    bottom: -80px;
    left: -120px;
    width: 350px;
    height: 350px;
    border-width: 1px;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  padding: 4rem 2rem 2rem 2rem;
  position: relative;
  z-index: 3;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 2rem;
    padding: 3rem 1.5rem 1.5rem 1.5rem;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
  color: ${props => props.isDark ? '#4299E1' : '#3182CE'};
  position: relative;
  font-weight: 600;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 40px;
    height: 3px;
    background: ${props => props.isDark ? '#63B3ED' : '#4299E1'};
    border-radius: 10px;
    box-shadow: ${props => props.isDark ? '0 0 8px rgba(99, 179, 237, 0.4)' : 'none'};
  }
`;

const FooterLink = styled(motion(Link))`
  color: ${props => props.isDark ? '#E2E8F0' : '#2D3748'};
  text-decoration: none;
  margin-bottom: 0.9rem;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  position: relative;
  padding-left: 0;
  
  &:before {
    content: '›';
    margin-right: 8px;
    color: ${props => props.isDark ? '#63B3ED' : '#4299E1'};
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s ease;
  }
  
  &:hover {
    color: ${props => props.isDark ? '#4299E1' : '#3182CE'};
    padding-left: 10px;
    
    &:before {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  color: white;
  background: ${props => props.theme.primary};
  font-size: 1.2rem;
  transition: all 0.3s ease;
  box-shadow: ${props => props.isDark 
    ? '0 4px 15px rgba(0, 0, 0, 0.3), 0 0 10px rgba(99, 179, 237, 0.1)' 
    : '0 4px 6px rgba(0, 0, 0, 0.1)'
  };
  
  &.facebook {
    background: #3b5998;
  }
  
  &.twitter {
    background: #1da1f2;
  }
  
  &.linkedin {
    background: #0077b5;
  }
  
  &.instagram {
    background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.isDark 
      ? '0 6px 20px rgba(0, 0, 0, 0.4), 0 0 15px rgba(99, 179, 237, 0.2)' 
      : '0 6px 10px rgba(0, 0, 0, 0.15)'
    };
  }
`;

const Newsletter = styled.div`
  margin-top: 1.5rem;
  position: relative;
`;

const NewsletterForm = styled(motion.form)`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const NewsletterInputContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
  
  &:focus-within svg {
    color: ${props => props.isDark ? '#4299E1' : '#3182CE'};
  }
`;

const NewsletterIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.isDark ? '#A0AEC0' : '#718096'};
  transition: color 0.3s ease;
`;

const NewsletterInput = styled.input`
  padding: 1rem 1rem 1rem 2.5rem;
  border: 2px solid transparent;
  background: ${props => props.isDark 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.05)'};
  color: ${props => props.isDark ? '#EDF2F7' : '#2D3748'};
  border-radius: 8px;
  width: 100%;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.isDark ? '#4299E1' : '#3182CE'};
    background: ${props => props.isDark 
      ? 'rgba(255, 255, 255, 0.15)' 
      : 'rgba(0, 0, 0, 0.03)'};
    box-shadow: ${props => props.isDark 
      ? '0 0 0 3px rgba(66, 153, 225, 0.3), 0 0 10px rgba(99, 179, 237, 0.1)' 
      : '0 0 0 3px rgba(66, 153, 225, 0.2)'
    };
  }
  
  &::placeholder {
    color: ${props => props.isDark ? '#A0AEC0' : '#718096'};
  }
`;

const NewsletterButton = styled(motion.button)`
  padding: 0.9rem;
  background: ${props => props.isDark ? '#4299E1' : '#3182CE'};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: ${props => props.isDark 
    ? '0 4px 10px rgba(0, 0, 0, 0.2), 0 0 10px rgba(99, 179, 237, 0.1)' 
    : '0 4px 10px rgba(0, 0, 0, 0.1)'
  };
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.isDark ? '#63B3ED' : '#4299E1'};
    box-shadow: ${props => props.isDark 
      ? '0 6px 15px rgba(0, 0, 0, 0.3), 0 0 15px rgba(99, 179, 237, 0.2)' 
      : '0 6px 15px rgba(0, 0, 0, 0.15)'
    };
  }
`;

const SuccessMessage = styled(motion.div)`
  background: ${props => props.isDark ? 'rgba(56, 161, 105, 0.9)' : '#2B9348'};
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  box-shadow: ${props => props.isDark ? '0 4px 10px rgba(0, 0, 0, 0.2)' : '0 4px 10px rgba(0, 0, 0, 0.1)'};
  
  svg {
    margin-right: 0.5rem;
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  font-size: 0.9rem;
  position: relative;
  z-index: 3;
  
  a {
    color: ${props => props.isDark ? '#4299E1' : '#3182CE'};
    text-decoration: none;
    font-weight: 500;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 1px;
      background: ${props => props.isDark ? '#4299E1' : '#3182CE'};
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.3s ease;
    }
    
    &:hover:after {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
`;

const ContactInfo = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  
  svg {
    margin-right: 0.8rem;
    color: ${props => props.isDark ? '#4299E1' : '#3182CE'};
    font-size: 1.2rem;
    transform: translateY(2px);
  }
`;

const FooterLogo = styled.div`
  margin-bottom: 1.5rem;
  
  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: ${props => props.isDark ? '#4299E1' : '#3182CE'};
    font-size: 1.5rem;
    font-weight: 700;
    
    span {
      display: inline-block;
      margin-left: 0.5rem;
    }
  }
  
  img {
    height: 3rem;
    filter: ${props => props.isDark ? 'drop-shadow(0 0 8px rgba(99, 179, 237, 0.3))' : 'none'};
  }
`;

const LogoTagline = styled.p`
  margin-top: 0.8rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  opacity: ${props => props.isDark ? 0.9 : 0.8};
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 3;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const FooterTermsLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 1rem;
  }
  
  a {
    color: ${props => props.isDark ? '#E2E8F0' : '#2D3748'};
    text-decoration: none;
    font-size: 0.9rem;
    opacity: 0.8;
    transition: all 0.3s ease;
    
    &:hover {
      color: ${props => props.isDark ? '#4299E1' : '#3182CE'};
      opacity: 1;
    }
  }
`;

const Footer = () => {
  const { theme, isDarkMode } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      // Ici vous pourriez ajouter une API call pour enregistrer l'email
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 3000);
    }
  };

  return (
    <FooterContainer isDark={isDarkMode}>
      {/* Décorations avec courbes */}
      <FooterWaveContainer>
        <FooterWaveTop isDark={isDarkMode} />
        <FooterWaveBottom isDark={isDarkMode} />
      </FooterWaveContainer>
      
      {/* Éléments décoratifs */}
      <FooterDecoration isDark={isDarkMode} className="top-right" />
      <FooterDecoration isDark={isDarkMode} className="bottom-left" />
      
      <FooterContent>
        <FooterSection>
          <FooterLogo isDark={isDarkMode}>
            <Link to="/">
              <img src={logos} alt="PropentaTech" />
              <span>PropentaTech</span>
            </Link>
          </FooterLogo>
          <LogoTagline isDark={isDarkMode}>
            Fournisseur de solutions technologiques innovantes, nous accompagnons les entreprises dans leur transformation digitale.
          </LogoTagline>
          <SocialIcons>
            <SocialIcon 
              href="https://facebook.com" 
              target="_blank" 
              rel="noopener noreferrer"
              theme={theme}
              isDark={isDarkMode}
              className="facebook"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <i className="fab fa-facebook-f"></i>
            </SocialIcon>
            <SocialIcon 
              href="https://twitter.com" 
              target="_blank" 
              rel="noopener noreferrer"
              theme={theme}
              isDark={isDarkMode}
              className="twitter"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <i className="fab fa-twitter"></i>
            </SocialIcon>
            <SocialIcon 
              href="https://linkedin.com" 
              target="_blank" 
              rel="noopener noreferrer"
              theme={theme}
              isDark={isDarkMode}
              className="linkedin"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <i className="fab fa-linkedin-in"></i>
            </SocialIcon>
            <SocialIcon 
              href="https://instagram.com" 
              target="_blank" 
              rel="noopener noreferrer"
              theme={theme}
              isDark={isDarkMode}
              className="instagram"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <i className="fab fa-instagram"></i>
            </SocialIcon>
          </SocialIcons>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle isDark={isDarkMode}>{t('quickLinks', 'Liens rapides')}</FooterTitle>
          <FooterLink to="/" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('home', 'Accueil')}</FooterLink>
          <FooterLink to="/about" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('about', 'À propos')}</FooterLink>
          <FooterLink to="/services" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('services', 'Services')}</FooterLink>
          <FooterLink to="/projects" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('projects', 'Projets')}</FooterLink>
          <FooterLink to="/blog" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('blog', 'Blog')}</FooterLink>
          <FooterLink to="/contact" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('contact', 'Contact')}</FooterLink>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle isDark={isDarkMode}>{t('services', 'Services')}</FooterTitle>
          <FooterLink to="/services/web-development" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('webDevelopment', 'Développement Web')}</FooterLink>
          <FooterLink to="/services/mobile-apps" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('mobileApps', 'Applications Mobiles')}</FooterLink>
          <FooterLink to="/services/ui-ux-design" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('uiUxDesign', 'Design UI/UX')}</FooterLink>
          <FooterLink to="/services/consulting" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('consulting', 'Consulting')}</FooterLink>
          <FooterLink to="/services/cloud-solutions" isDark={isDarkMode} whileHover={{ x: 5 }}>{t('cloudSolutions', 'Solutions Cloud')}</FooterLink>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle isDark={isDarkMode}>{t('contact', 'Contact')}</FooterTitle>
          <ContactInfo isDark={isDarkMode}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
            </svg>
            <span>Château Ngoa-Ekelle Yaoundé, Cameroun</span>
          </ContactInfo>
          <ContactInfo isDark={isDarkMode}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
            </svg>
            <span>+237 657 097 369 / 690 232 120</span>
          </ContactInfo>
          <ContactInfo isDark={isDarkMode}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
            </svg>
            <span>propentatech@gmail.com</span>
          </ContactInfo>
          
          <FooterTitle isDark={isDarkMode} style={{ marginTop: '2rem' }}>{t('newsletter', 'Newsletter')}</FooterTitle>
          <p>{t('newsletterDescription', 'Inscrivez-vous pour recevoir nos actualités et offres spéciales.')}</p>
          <Newsletter>
            <NewsletterForm onSubmit={handleSubmit}>
              <NewsletterInputContainer isDark={isDarkMode}>
                <NewsletterIcon isDark={isDarkMode}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                  </svg>
                </NewsletterIcon>
                <NewsletterInput 
                  type="email" 
                  placeholder={t('emailPlaceholder', 'Votre email')} 
                  isDark={isDarkMode}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </NewsletterInputContainer>
              <NewsletterButton 
                type="submit"
                isDark={isDarkMode}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {t('subscribe', 'S\'abonner')}
              </NewsletterButton>
            </NewsletterForm>
            
            <AnimatePresence>
              {subscribed && (
                <SuccessMessage
                  isDark={isDarkMode}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                  </svg>
                  <span>Merci pour votre inscription!</span>
                </SuccessMessage>
              )}
            </AnimatePresence>
          </Newsletter>
        </FooterSection>
      </FooterContent>
      
      <FooterBottom>
        <Copyright isDark={isDarkMode}>
          © {new Date().getFullYear()} <a href="/"><strong>PropentaTech Saving Solutions</strong></a>. Tous droits réservés.
        </Copyright>
        
        <FooterTermsLinks isDark={isDarkMode}>
          <a href="/privacy">Politique de confidentialité</a>
          <a href="/terms">Conditions d'utilisation</a>
          <a href="/cookies">Cookies</a>
        </FooterTermsLinks>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;